import { Card, ComponentState, Divider, Empty, Flex, Skeleton, Text } from "components/common"
import { IconText } from "components/custom"
import { useGetSalesPlaybookQuery } from "features/leads/api"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import theme from "utils/themeVars"
import CategoryInsights from "./CategoryInsights"
import LastEngagement from "./LastEngagement"
import QuickHighlights from "./QuickHighlights"
import styles from "./SalesPlaybook.module.less"
import cx from "clsx"

const SalesPlaybook = ({ clientId }) => {
  const { t } = useTranslation()

  const {
    data: playbookResponse,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetSalesPlaybookQuery(clientId, {
    skip: !clientId,
  })

  const playbookData = playbookResponse?.data
  const { lastEngagement, quickInsights, categoryBasedInsights, hasPlaybookContent } = useMemo(() => {
    const engagement = playbookData?.lastEngagement || null
    const salesPlaybook = playbookData?.salesPlaybook || {}
    const insights = Array.isArray(salesPlaybook?.quickInsights) ? salesPlaybook.quickInsights : []
    const categoryInsights =
      salesPlaybook?.categoryBasedInsights && typeof salesPlaybook.categoryBasedInsights === "object"
        ? salesPlaybook.categoryBasedInsights
        : null

    return {
      lastEngagement: engagement,
      quickInsights: insights,
      categoryBasedInsights: categoryInsights,
      hasPlaybookContent: insights.length > 0 || (categoryInsights && Object.keys(categoryInsights).length > 0),
    }
  }, [playbookData])

  const hasQuickInsights = quickInsights.length > 0
  const hasCategoryInsights = categoryBasedInsights && Object.keys(categoryBasedInsights).length > 0

  if (isFetching) {
    return (
      <Flex vertical gap={"16px"} className="w-100">
        <Skeleton height={100} block width="100%" />

        <Skeleton height={500} block width="100%" />
      </Flex>
    )
  }

  if (isError) {
    return (
      <div className="p-24 w-100">
        <ComponentState
          description={error?.message || t("Unable to load sales playbook")}
          status={error?.status}
          btnLoading={isFetching}
          onClick={refetch}
        />
      </div>
    )
  }

  if (!playbookData || typeof playbookData !== "object") {
    return (
      <div className="p-24 w-100">
        <Empty
          description={t("No sales playbook data available for this client yet.")}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    )
  }

  return (
    <div className={styles.salesPlaybookContainer}>
      <Flex vertical gap={16}>
        <LastEngagement engagement={lastEngagement} />
        {hasPlaybookContent ? (
          <Card Shadow={false} className={cx(styles.salesPlaybookCard, "mbe-8")}>
            <Flex align="center" gap={8} className={styles.salesPlaybookHeader}>
              <IconText
                icon="BsBook"
                iconProps={{ size: 18, style: { color: theme["accent-color"] } }}
                title={
                  <Text strong className="fs16">
                    {t("Sales Playbook")}
                  </Text>
                }
              />
            </Flex>
            <Flex vertical gap={"8px"} className={styles.salesPlaybookBody}>
              {hasQuickInsights && <QuickHighlights insights={quickInsights} />}
              {hasQuickInsights && hasCategoryInsights && <Divider />}
              {hasCategoryInsights && <CategoryInsights categoryInsights={categoryBasedInsights} />}
            </Flex>
          </Card>
        ) : (
          <Flex justify="center" align="center" className="w-100" style={{ minHeight: 200 }}>
            <Empty description={t("No playbook insights available yet.")} />
          </Flex>
        )}
      </Flex>
    </div>
  )
}

export default SalesPlaybook
